import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Check, Loader2, Stethoscope, ArrowRight } from "lucide-react";
import { useListFacilityTypes, API_BASE } from "@/lib/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

export const FACILITY_TYPE_LIST = [
  {
    name: "Individual Practice",
    description: "Healthcare practice operated by a single healthcare provider, typically serving patients directly in an outpatient setting."
  },
  {
    name: "Urgent Care",
    description: "Walk-in healthcare facility providing prompt treatment for non-life-threatening illnesses and injuries that require same-day attention."
  },
  {
    name: "Clinical Laboratory",
    description: "Facility that performs diagnostic testing and analysis of blood, urine, tissue, and other patient specimens."
  },
  {
    name: "Multi Speciality",
    description: "Healthcare facility offering services from multiple medical specialties, often providing coordinated care in one location."
  },
  {
    name: "Hospital",
    description: "Healthcare facility providing comprehensive inpatient and outpatient services, including emergency care, surgery, diagnosis, and treatment."
  },
  {
    name: "Imaging and Diagnostic Center",
    description: "Facility providing diagnostic services such as X-rays, CT scans, MRI, ultrasound, mammography, and other tests."
  },
  {
    name: "Group Practice",
    description: "Medical practice where multiple healthcare providers work together and share administrative or clinical resources."
  },
  {
    name: "Other Qualified Health Center",
    description: "Community-based healthcare facility that provides a range of medical or supportive health services and does not fit the other defined facility categories."
  },
  {
    name: "Pharmacy",
    description: "Facility that dispenses prescription and over-the-counter medications and may provide additional pharmacy-related health services."
  },
  {
    name: "Other Health Center",
    description: "Healthcare facility providing medical or health-related services that do not fall into the other specified facility types."
  }
];

export default function FacilityTypePromptModal({ user, onComplete }: { user: any; onComplete?: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: dbFacilityTypes = [] } = useListFacilityTypes();

  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Popup auto-prompt disabled per requirements
    setOpen(false);
  }, [user]);

  const activeFacilityTypes = Array.isArray(dbFacilityTypes) && dbFacilityTypes.length > 0 ? dbFacilityTypes : FACILITY_TYPE_LIST;

  const handleSave = async () => {
    if (!selectedType) {
      toast({
        variant: "destructive",
        title: "Please Select a Facility Type",
        description: "Select the option that best describes your practice model."
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const matched = activeFacilityTypes.find((ft: any) => ft.name === selectedType);
      
      const payload = {
        facilityType: selectedType,
        facilityTypeId: matched?.id || undefined
      };

      const res = await fetch(`${API_BASE}/providers/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (json.success) {
        toast({
          title: "Facility Type Saved!",
          description: `Your practice classification set to ${selectedType}.`
        });

        // Update local user state in localStorage
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            parsed.facilityType = selectedType;
            if (parsed.provider) parsed.provider.facilityType = selectedType;
            localStorage.setItem("user", JSON.stringify(parsed));
          } catch (e) { }
        }

        queryClient.invalidateQueries({ queryKey: ["getMe"] });
        setOpen(false);
        if (onComplete) onComplete();
      } else {
        throw new Error(json.error || "Failed to save facility type");
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error Saving Facility Type",
        description: err.message || "Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem("facility_type_prompt_dismissed", "true");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleDismiss(); }}>
      <DialogContent className="sm:max-w-2xl rounded-3xl p-6 max-h-[90vh] flex flex-col">
        <DialogHeader className="space-y-1.5 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-xl font-black tracking-tight text-foreground flex items-center gap-2">
                Select Practice Facility Type
                <Badge variant="outline" className="text-[10px] font-bold text-primary border-primary/30 bg-primary/5">
                  Required Profile Setup
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Please classify your healthcare facility or practice model to optimize search and patient bookings.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-2.5 pr-1">
          {activeFacilityTypes.map((item: any) => {
            const isSelected = selectedType === item.name;
            return (
              <div
                key={item.name}
                onClick={() => setSelectedType(item.name)}
                className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-start gap-3 ${
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary/30"
                    : "border-border/70 hover:border-primary/40 bg-card hover:bg-accent/40"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                    isSelected ? "border-primary bg-primary text-white" : "border-muted-foreground/30 bg-background"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm text-foreground">{item.name}</span>
                    {isSelected && (
                      <Badge className="bg-primary text-primary-foreground font-bold text-[9px] uppercase">
                        Selected
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-normal font-medium">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="pt-3 border-t border-border flex sm:justify-between items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={handleDismiss}
            className="text-xs text-muted-foreground rounded-xl"
          >
            Remind Me Later
          </Button>

          <Button
            type="button"
            onClick={handleSave}
            disabled={!selectedType || isSubmitting}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-extrabold text-xs px-6 py-5 rounded-2xl gap-2 shadow-md"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Save & Continue <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
